import { useState, useCallback } from 'react';

// https://github.com/streamich/react-use/blob/master/src/useSessionStorage.ts
export const useSessionStorage = <T>(
    key: string,
    initialValue?: T,
    raw?: boolean,
): [T, (value: T) => void] => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [state, setState] = useState<T>(() => {
        try {
            const sessionStorageValue = sessionStorage.getItem(key);
            if (typeof sessionStorageValue !== 'string') {
                sessionStorage.setItem(key, raw ? String(initialValue) : JSON.stringify(initialValue));
                return initialValue;
            }
            return raw ? sessionStorageValue : JSON.parse(sessionStorageValue || 'null') as T;
        } catch {
            // If user is in private mode or has storage restriction
            // sessionStorage can throw. JSON.parse and JSON.stringify
            // cat throw, too.
            return initialValue;
        }
    });

    const handle = useCallback((value: T) => {
        try {
            setState(value);
            const serializedState = raw ? String(value) : JSON.stringify(value);
            sessionStorage.setItem(key, serializedState);
        } catch {
            // If user is in private mode or has storage restriction
            // sessionStorage can throw. Also JSON.stringify can throw.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setState]);

    return [state, handle];
};

export const removeSessionStorage = (keys: Array<string>): void => {
    keys.forEach((key) => sessionStorage.removeItem(key));
};
